
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, ArrowRight, X, ChevronRight, ChevronLeft, Info } from 'lucide-react';
import { visualQaExperimentalApiHost } from './config/apiConfig';
import VisualQaHandler from "./helpers/visualQaHandler";
import { getStage, getRegion } from './helpers/appUtils.js';

// VisualQA Logo Component
const VisualQALogo = () => (
  <div className="flex items-center mr-4">
    <span className="text-blue font-medium text-2xl">Visual</span>
    <span className="text-graphite-gray font-medium text-2xl">QA</span>
  </div>
);

// Enlarge Image Modal Component
const EnlargeImageModal = ({ show, onClose, imageUrl }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-graphite-gray bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-3xl w-full max-h-[90vh] overflow-auto bg-bone-white rounded-lg">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-graphite-gray bg-bone-white rounded-full hover:bg-gray-200 transition-all duration-200 ease-in-out active:scale-[0.99]"
        >
          <X size={20} />
        </button>
        <img
          src={imageUrl}
          alt="Enlarged Preview"
          className="w-full h-auto rounded-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/600x400/cccccc/333333?text=Image+Load+Error`;
          }}
        />
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ show, onClose, imageUrlInput, setImageUrlInput, displayedImageUrl, setDisplayedImageUrl, openEnlargeModal, setUploadedImageUrl }) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrlInput(url);
    if (url.trim() !== '') {
      setDisplayedImageUrl(url);
    } else {
      setDisplayedImageUrl('');
    }
  };
  const handleDoneClick = () => {
    setUploadedImageUrl(imageUrlInput);
    onClose();
  };
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-graphite-gray hover:text-blue hover:bg-blue/10 rounded-full p-1 transition-all duration-200 ease-in-out active:scale-[0.99]"
        >
          <X size={24} />
        </button>
        <h2 className="text-2xl font-medium text-graphite-gray mb-6">Upload Image (URL)</h2>

        <div className="mb-6 relative">
          <input
            type="text"
            id="imageUrl"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none  transition-all duration-200 ease-in-out"
            placeholder={isInputFocused || imageUrlInput ? '' : 'Enter image URL here'}
            value={imageUrlInput}
            onChange={handleImageUrlChange}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
        </div>

        {displayedImageUrl && (
          <div className="mb-6 text-center">
            <p className="text-sm font-medium text-graphite-gray mb-2">Preview:</p>
            <img
              src={displayedImageUrl}
              alt="Preview"
              className="w-full h-40 object-cover rounded-lg border border-gray-300 cursor-pointer"
              onClick={() => openEnlargeModal(displayedImageUrl)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/200x150/cccccc/333333?text=Image+Load+Error`;
              }}
            />
          </div>
        )}

        <p className="text-sm text-graphite-gray text-center mt-4 leading-relaxed">
          Note: Actual image file upload is not available in this demo.
        </p>
        <div className="flex justify-end mt-6">
          <button
            onClick={handleDoneClick}
            className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer border border-blue/30 rounded-lg transition-all duration-200 ease-in-out active:scale-[0.99]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// LoadingSpinner.jsx
const LoadingSpinner = ({ loadingText = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center flex-grow w-full">
    <div className="w-16 h-16 border-4 border-t-4 border-t-gray-500 border-gray-200 rounded-full animate-spin mb-4"></div>
    <p className="text-lg font-medium text-graphite-gray-600">{loadingText}</p>
  </div>
);

// Initial Search Screen (adapted to match header search bar)
const InitialSearchScreen = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-bone-white text-graphite-gray p-4">
    <VisualQALogo />
    <div className="w-full max-w-2xl flex items-center p-2 mb-4">
      <div className="flex-grow bg-bone-white/50 border border-gray-200 rounded-lg flex items-center p-0 backdrop-blur-md mt-6">
        <input
          type="text"
          placeholder="Ask anything..."
          className="flex-grow bg-transparent text-graphite-gray placeholder-gray-500 focus:outline-none px-4 py-3 text-lg "
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {uploadedImageUrl && (
          <img
            src={uploadedImageUrl}
            alt="Preview"
            className="w-8 h-8 object-cover rounded-full ml-2 mr-2"
            onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/32x32/cccccc/333333?text=X`; }}
          />
        )}
        <div className="flex space-x-2 pr-2">
          <button className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]" onClick={openUploadModal}>
            <Upload size={20} />
          </button>
          <button onClick={() => handleSearchSubmit(true)} className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]">
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        Couldn’t find that. Let’s reframe the question.
      </p>
    )}
    <div className="flex justify-center space-x-6 mt-16 text-graphite-gray text-sm">
      <p>Made by MMIR in Amazon SJC</p>
    </div>
  </div>
);

  // Main Interaction Screen (combining header and dynamic content)
  const MainInteractionScreen = ({ activeTab, setActiveTab, searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, setHighlightedSection, highlightedSection, scrollToSectionAndHighlight, mmRetrievalRef, groundingRef, mmRetrievalGroundingRef, qaResponses}) => (
    <div className="flex flex-col flex-grow bg-bone-white text-graphite-gray min-h-screen"> 
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        errorMessage={errorMessage}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        setShowResults={setShowResults}
        setHighlightedSection={setHighlightedSection}
      />
      <div className="w-full max-w-2xl mx-auto py-10 px-1"> 
        {activeTab === 'customer' ?
            <CustomerResultsContent
              setActiveTab={setActiveTab}
              scrollToSectionAndHighlight={scrollToSectionAndHighlight}
              mmRetrievalRef={mmRetrievalRef} // Pass down refs for scrolling and highlighting
              groundingRef={groundingRef}
              mmRetrievalGroundingRef={mmRetrievalGroundingRef}
              qaResponses={qaResponses}
            />
            :
            <PrimitiveResultsContent
              highlightedSection={highlightedSection} // Pass highlight state
              mmRetrievalRef={mmRetrievalRef} // Pass down refs for scrolling and highlighting
              groundingRef={groundingRef}
              mmRetrievalGroundingRef={mmRetrievalGroundingRef}
              qaResponses={qaResponses}
            />
        }
      </div>
      {/* Modals are now rendered at the App level */}
    </div>
  );

// Common Header component for interaction screens
const Header = ({ activeTab, setActiveTab, searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, setHighlightedSection }) => (
  <div className="flex flex-col w-full bg-bone-white border-b border-gray-200 text-graphite-gray p-4">
    {/* Top search bar for new queries - consolidated into one set */}
    <div className="w-full flex items-center mb-4">
      <VisualQALogo />
      <div className="flex-grow bg-bone-white/50 border border-gray-200 rounded-lg flex items-center p-0 backdrop-blur-md">
        <input
          type="text"
          placeholder="Ask anything..."
          className="flex-grow bg-transparent text-graphite-gray placeholder-gray-500 focus:outline-none px-4 py-3 text-lg "
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {uploadedImageUrl && (
          <img
            src={uploadedImageUrl}
            alt="Preview"
            className="w-8 h-8 object-cover rounded-full ml-2 mr-2"
            onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/32x32/cccccc/333333?text=X`; }}
          />
        )}
        <div className="flex space-x-2 pr-2">
          <button className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]" onClick={openUploadModal}>
            <Upload size={20} />
          </button>
          <button onClick={() => handleSearchSubmit(false)} className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]">
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        Couldn’t find that. Let’s reframe the question.
      </p>
    )}
    {/* Customer/Primitive tabs - no background squares, just bottom border */}
    <div className="flex items-center justify-start space-x-6">
      <button
        onClick={() => { setActiveTab('customer'); setShowResults(true); setHighlightedSection([]); }}
        className={`px-4 py-2 text-sm font-medium text-graphite-gray hover:text-blue focus:outline-none transition-all duration-200 ease-in-out active:scale-[0.99] ${activeTab === 'customer' ? 'border-b-2 border-blue' : ''}`}
      >
        customer
      </button>
      <button
        onClick={() => { setActiveTab('primitive'); setShowResults(true); setHighlightedSection([]); }}
        className={`px-4 py-2 text-sm font-medium text-graphite-gray hover:text-blue focus:outline-none transition-all duration-200 ease-in-out active:scale-[0.99] ${activeTab === 'primitive' ? 'border-b-2 border-blue' : ''}`}
      >
        primitive
      </button>
    </div>
  </div>
);

// Content for the 'customer' tab (previously Step 2 content)
const CustomerResultsContent = ({ setActiveTab, scrollToSectionAndHighlight, mmRetrievalRef, groundingRef, mmRetrievalGroundingRef, qaResponses }) => (
  <div className="w-full space-y-4">
    {/* LLM Section */}
    <div className="px-4 py-2 space-y-2">
      <div className="flex items-center relative z-20">
          <h3 className="text-lg font-medium text-graphite-gray">Parametric Knowledge</h3>
          <div className="relative inline-block ml-2 group">
            <div className="cursor-help text-graphite-gray hover:text-blue">
              <Info size={16} />
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 w-48 bg-white text-graphite-gray text-xs p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
              Nova Pro relies on its parametric knowledge.
            </div>
          </div>
        </div>
      <p className="text-sm text-blue leading-relaxed">{qaResponses?.parametric?.mmlm_answer || 'No answer available'}</p>
    </div>

    {/* LLM + Grounding Section */}
    <div className="px-4 py-2 flex items-center justify-between">
      <div className="space-y-2">
        <div className="flex items-center relative z-20">
          <h3 className="text-lg font-medium text-graphite-gray">Parametric Knowledge with Text Grounding</h3>
          <div className="relative inline-block ml-2 group">
            <div className="cursor-help text-graphite-gray hover:text-blue">
              <Info size={16} />
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 w-48 bg-white text-graphite-gray text-xs p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
              Nova Pro leverages NGS text grounding.
            </div>
          </div>
        </div>
        <p className="text-sm text-blue leading-relaxed">{qaResponses?.parametricWithNgs?.mmlm_answer || 'No answer available'}</p>
      </div>
      <button
        onClick={() => {
          setActiveTab('primitive');
          setTimeout(() => scrollToSectionAndHighlight(groundingRef, 'grounding'), 100);
        }}
        className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer border border-blue/30 rounded-lg transition-all duration-200 ease-in-out active:scale-[0.99]"
      >
        X-Ray
      </button>
    </div>

    {/* LLM + MM Retrieval Section */}
    <div className="px-4 py-2 flex items-center justify-between">
      <div className="space-y-2">
        <div className="flex items-center relative z-20">
          <h3 className="text-lg font-medium text-graphite-gray">Parametric Knowledge with Image Grounding</h3>
          <div className="relative inline-block ml-2 group">
            <div className="cursor-help text-graphite-gray hover:text-blue">
              <Info size={16} />
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 w-48 bg-white text-graphite-gray text-xs p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
              Nova Pro leverages images with their captions from MMIR's reverse image search.
            </div>
          </div>
        </div>
        <p className="text-sm text-blue leading-relaxed">{qaResponses?.parametricWithMmir?.mmlm_answer || 'No answer available'}</p>
      </div>
      <button
        onClick={() => {
          setActiveTab('primitive');
          setTimeout(() => scrollToSectionAndHighlight(mmRetrievalRef, 'mmRetrieval'), 100);
        }}
        className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer border border-blue/30 rounded-lg transition-all duration-200 ease-in-out active:scale-[0.99]"
      >
        X-Ray
      </button>
    </div>

    {/* LLM + MM Retrieval + Grounding Section */}
    <div className="px-4 py-2 flex items-center justify-between">
      <div className="space-y-2">
        <div className="flex items-center relative z-20">
          <h3 className="text-lg font-medium text-graphite-gray">Parametric Knowledge with Image & Text Grounding</h3>
          <div className="relative inline-block ml-2 group">
            <div className="cursor-help text-graphite-gray hover:text-blue">
              <Info size={16} />
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 w-48 bg-white text-graphite-gray text-xs p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
              Nova Pro leverages NGS text grounding as well as images with their captions from MMIR's reverse image search requested with NGS text grounding.
            </div>
          </div>
        </div>
        <p className="text-sm text-blue leading-relaxed">{qaResponses?.parametricWithMmirNgs?.mmlm_answer || 'No answer available'}</p>
      </div>
      <button
        onClick={() => {
          setActiveTab('primitive');
          setTimeout(() => {
            scrollToSectionAndHighlight(mmRetrievalGroundingRef, ['mmRetrievalGrounding']);
          }, 100);
        }}
        className="p-2 text-graphite-gray hover:text-blue hover:bg-gray-100 cursor-pointer border border-blue/30 rounded-lg transition-all duration-200 ease-in-out active:scale-[0.99]"
      >
        X-Ray
      </button>
    </div>
  </div>
);

// Content for the 'primitive' tab (previously Step 3 content)
const PrimitiveResultsContent = ({ highlightedSection, mmRetrievalRef, mmRetrievalGroundingRef, groundingRef, qaResponses }) => {
  const mmRetrievalCarouselRef = useRef(null);
  const mmRetrievalGroundingCarouselRef = useRef(null);

  const [mmRetrievalScrollPosition, setMmRetrievalScrollPosition] = useState(0);
  const [mmRetrievalScrollWidth, setMmRetrievalScrollWidth] = useState(0);
  const [mmRetrievalClientWidth, setMmRetrievalClientWidth] = useState(0);

  const [mmRetrievalGroundingScrollPosition, setMmRetrievalGroundingScrollPosition] = useState(0);
  const [mmRetrievalGroundingScrollWidth, setMmRetrievalGroundingScrollWidth] = useState(0);
  const [mmRetrievalGroundingClientWidth, setMmRetrievalGroundingClientWidth] = useState(0);

  // Using useCallback for stable event handler references
  const handleMmRetrievalScroll = useCallback((e) => {
    setMmRetrievalScrollPosition(e.target.scrollLeft);
    setMmRetrievalScrollWidth(e.target.scrollWidth);
    setMmRetrievalClientWidth(e.target.clientWidth);
  }, []);

  const handleMmRetrievalGroundingScroll = useCallback((e) => {
    setMmRetrievalGroundingScrollPosition(e.target.scrollLeft);
    setMmRetrievalGroundingScrollWidth(e.target.scrollWidth);
    setMmRetrievalGroundingClientWidth(e.target.clientWidth);
  }, []);

  useEffect(() => {
    const mmRetRefCurrent = mmRetrievalCarouselRef.current;
    const mmGroundRefCurrent = mmRetrievalGroundingCarouselRef.current;

    const initializeCarousel = (ref, setScrollPos, setScrollWidth, setClientWidth, handleScrollCb) => {
      if (ref) {
        // Set initial values
        setScrollPos(ref.scrollLeft);
        setScrollWidth(ref.scrollWidth);
        setClientWidth(ref.clientWidth);
        // Add event listener
        ref.addEventListener('scroll', handleScrollCb);
      }
    };

    initializeCarousel(mmRetRefCurrent, setMmRetrievalScrollPosition, setMmRetrievalScrollWidth, setMmRetrievalClientWidth, handleMmRetrievalScroll);
    initializeCarousel(mmGroundRefCurrent, setMmRetrievalGroundingScrollPosition, setMmRetrievalGroundingScrollWidth, setMmRetrievalGroundingClientWidth, handleMmRetrievalGroundingScroll);

    // Cleanup event listeners
    return () => {
      if (mmRetRefCurrent) {
        mmRetRefCurrent.removeEventListener('scroll', handleMmRetrievalScroll);
      }
      if (mmGroundRefCurrent) {
        mmGroundRefCurrent.removeEventListener('scroll', handleMmRetrievalGroundingScroll);
      }
    };
  }, [mmRetrievalCarouselRef.current, mmRetrievalGroundingCarouselRef.current, handleMmRetrievalScroll, handleMmRetrievalGroundingScroll]);

  const scrollImages = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = ref.current.clientWidth * 0.8;
      const newScrollLeft = ref.current.scrollLeft + direction * scrollAmount;
      ref.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  // Determine if arrows should be greyed out
  const isMmRetrievalAtStart = mmRetrievalScrollPosition <= 1;
  const isMmRetrievalAtEnd = mmRetrievalScrollWidth - mmRetrievalScrollPosition - mmRetrievalClientWidth <= 1;

  const isMmGroundingAtStart = mmRetrievalGroundingScrollPosition <= 1;
  const isMmGroundingAtEnd = mmRetrievalGroundingScrollWidth - mmRetrievalGroundingScrollPosition - mmRetrievalGroundingClientWidth <= 1;

  const arrowButtonClasses = (isDisabled) =>
    `p-2 rounded-full bg-bone-white transition-all duration-200 ease-in-out active:scale-[0.99] ${isDisabled ? 'text-gray-300 cursor-not-allowed opacity-50' : 'text-graphite-gray hover:text-blue hover:bg-gray-100'}`;

  return (
    <div className="w-full space-y-4">
      {/* MM Retrieval Section */}
      <div ref={mmRetrievalRef} className={`px-4 py-2 space-y-2 transition-all duration-500 ${highlightedSection.includes('mmRetrieval') ? 'relative z-10 before:absolute before:-inset-x-14 before:-inset-y-2 before:bg-yellow-100/50 before:rounded-md before:transition-all before:duration-500' : ''}`}>
        <h3 className="text-lg font-medium text-graphite-gray relative z-20">
          MMIR Reverse Search Output
        </h3>
        <div className="flex items-center relative -mx-4">
          <button
            className={`absolute -left-12 top-1/2 -translate-y-1/2 z-10 ${arrowButtonClasses(isMmRetrievalAtStart)}`}
            onClick={() => scrollImages(mmRetrievalCarouselRef, -1)}
            disabled={isMmRetrievalAtStart}
          >
            <ChevronLeft size={24} />
          </button>
          <div
            ref={mmRetrievalCarouselRef}
            className="flex flex-grow overflow-x-auto space-x-4 py-2 px-4 scrollbar-hide"
            onScroll={handleMmRetrievalScroll}
          >
            {qaResponses?.parametricWithMmir?.mmir_images?.length > 0 ? 
              qaResponses.parametricWithMmir.mmir_images.map((image, i) => (
                <div key={i} className="flex-none w-48 h-24 bg-gray-200 rounded-md overflow-hidden">
                  <img 
                    src={image.url} 
                    alt={image.caption || `Image ${i+1}`} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png?20210521171500"; 
                      e.target.parentNode.classList.add("flex", "items-center", "justify-center");
                      e.target.parentNode.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h12c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1z"/></svg>';
                    }}
                  />
                </div>
              )) : 
              [...Array(20)].map((_, i) => (
                <div key={i} className="flex-none w-48 h-24 bg-gray-200 rounded-md flex items-center justify-center text-graphite-gray">
                  <X size={24} />
                </div>
            ))}
          </div>
          <button
            className={`absolute -right-12 top-1/2 -translate-y-1/2 z-10 ${arrowButtonClasses(isMmRetrievalAtEnd)}`}
            onClick={() => scrollImages(mmRetrievalCarouselRef, 1)}
            disabled={isMmRetrievalAtEnd}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      {/* MM Retrieval + Grounding Section */}
      <div ref={mmRetrievalGroundingRef} className={`px-4 py-2 space-y-2 transition-all duration-500 ${highlightedSection.includes('mmRetrievalGrounding') ? 'relative z-10 before:absolute before:-inset-x-14 before:-inset-y-2 before:bg-yellow-100/50 before:rounded-md before:transition-all before:duration-500' : ''}`}>
        <h3 className="text-lg font-medium text-graphite-gray relative z-20">
          MMIR Reverse Search Output with NGS Text Grounding
        </h3>
        <div className="flex items-center relative -mx-4">
          <button
            className={`absolute -left-12 top-1/2 -translate-y-1/2 z-10 ${arrowButtonClasses(isMmGroundingAtStart)}`}
            onClick={() => scrollImages(mmRetrievalGroundingCarouselRef, -1)}
            disabled={isMmGroundingAtStart}
          >
            <ChevronLeft size={24} />
          </button>
          <div
            ref={mmRetrievalGroundingCarouselRef}
            className="flex flex-grow overflow-x-auto space-x-4 py-2 px-4 scrollbar-hide"
            onScroll={handleMmRetrievalGroundingScroll}
          >
            {qaResponses?.parametricWithMmirNgs?.mmir_images?.length > 0 ? 
              qaResponses.parametricWithMmirNgs.mmir_images.map((image, i) => (
                <div key={i} className="flex-none w-48 h-24 bg-gray-200 rounded-md overflow-hidden">
                  <img 
                    src={image.url} 
                    alt={image.caption || `Image ${i+1}`} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png?20210521171500"; 
                      e.target.parentNode.classList.add("flex", "items-center", "justify-center");
                      e.target.parentNode.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h12c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1z"/></svg>';
                    }}
                  />
                </div>
              )) : 
              [...Array(20)].map((_, i) => (
                <div key={i} className="flex-none w-48 h-24 bg-gray-200 rounded-md flex items-center justify-center text-graphite-gray">
                  <X size={24} />
                </div>
            ))}
          </div>
          <button
            className={`absolute -right-12 top-1/2 -translate-y-1/2 z-10 ${arrowButtonClasses(isMmGroundingAtEnd)}`}
            onClick={() => scrollImages(mmRetrievalGroundingCarouselRef, 1)}
            disabled={isMmGroundingAtEnd}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      {/* Grounding Section */}
      <div ref={groundingRef} className={`px-4 py-2 space-y-2 transition-all duration-500 ${highlightedSection.includes('grounding') ? 'relative z-10 before:absolute before:-inset-x-14 before:-inset-y-2 before:bg-yellow-100/50 before:rounded-md before:transition-all before:duration-500' : ''}`}>
        <h3 className="text-lg font-medium text-graphite-gray relative z-20">NGS Text Grounding</h3>
        <p className="text-sm text-blue relative z-20 leading-relaxed">{qaResponses?.parametricWithMmirNgs?.llm_answer_from_ngs_grounding || 'No grounding available'}</p>
      </div>
    </div>
  );
};

// Main App component
const App = () => {
  let currentStage = getStage() || 'DEV';
  const [stage, setStage] = useState(currentStage);
  const [region, setRegion] = useState(getRegion(currentStage) || 'us-west-2');

  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState('customer');
  const [showResults, setShowResults] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');

  // Effect to update isUploaded based on uploadedImageUrl
  const [isUploaded, setIsUploaded] = useState(false); // Define isUploaded state here
  useEffect(() => {
    setIsUploaded(uploadedImageUrl.trim() !== '');
  }, [uploadedImageUrl]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [displayedImageUrl, setDisplayedImageUrl] = useState('');

  const [showEnlargeModal, setShowEnlargeModal] = useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState('');

  const mmRetrievalRef = useRef(null);
  const mmRetrievalGroundingRef = useRef(null);
  const groundingRef = useRef(null);

  const [visualQaHandler, setVisualQaHandler] = useState(null);
  const [qaResponses, setQaResponses] = useState({
    parametricWithMmirNgs: null,
    parametricWithMmir: null,
    parametricWithNgs: null,
    parametric: null
  });

  useEffect(() => {
    setVisualQaHandler(new VisualQaHandler(visualQaExperimentalApiHost[stage][region]))
  }, [stage, region]);

  const scrollToSectionAndHighlight = (ref, sectionNames) => {
    const namesToHighlight = Array.isArray(sectionNames) ? sectionNames : [sectionNames];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightedSection(namesToHighlight);
      setTimeout(() => {
        setHighlightedSection([]);
      }, 2000);
    }
  };

  const handleSearchSubmit = async (fromInitialScreen = false) => {
    if (searchTerm.trim() === '' && uploadedImageUrl.trim() === '') {
      setErrorMessage('Please enter a query or upload a file.');
      return;
    }
    setErrorMessage('');
    setIsLoading(true);

    const loadingMessages = [
      "Retrieving text grounding",
      "Retrieving image grounding",
      "Prompting LLM"
    ];
    let messageIndex = 0;
    setLoadingText(loadingMessages[messageIndex]);

    const intervalId = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[messageIndex]);
    }, 3000);

    try {
      let responses = await visualQaHandler.getVisualQaExperimentalResponses(searchTerm, imageUrlInput);
      
      // Clear the interval and update state immediately after receiving response
      clearInterval(intervalId);
      setIsLoading(false);
      setQaResponses(responses);
      
      if (fromInitialScreen) {
        setCurrentStep(1);
        setShowResults(true);
        setHighlightedSection([]);
      } else {
        setShowResults(true);
        setHighlightedSection([]);
        if (activeTab !== 'customer') {
          setActiveTab('customer');
        }
      }
    } catch (error) {
      clearInterval(intervalId);
      setIsLoading(false);
      setErrorMessage('Error fetching responses: ' + (error?.message || 'Unknown error'));
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setImageUrlInput('');
    setDisplayedImageUrl('');
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
  };

  const openEnlargeModal = (url) => {
    setEnlargedImageUrl(url);
    setShowEnlargeModal(true);
  };

  const closeEnlargeModal = () => {
    setShowEnlargeModal(false);
    setEnlargedImageUrl('');
  };

  return (
    <div className="font-sans antialiased text-graphite-gray bg-bone-white min-h-screen flex flex-col">
      <script src="https://cdn.tailwindcss.com"></script>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;700&display=swap" rel="stylesheet" />
      <style>
        {`
          body {
            font-family: 'Space Grotesk', sans-serif;
            line-height: 1.6;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
        `}
      </style>
      <script>
        {`
          tailwind.config = {
            theme: {
              extend: {
                colors: {
                  'bone-white': '#FAFAFA',
                  'graphite-gray': '#1C1C1C',
                  'blue': '#33A4B5',
                  'light-blue-highlight': '#ADD8E6',
                },
                fontFamily: {
                  sans: ['"Space Grotesk"', 'sans-serif'],
                },
                keyframes: {
                  spin: {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  }
                },
                animation: {
                  spin: 'spin 1s linear infinite',
                },
              }
            }
          }
        `}
      </script>
      {isLoading ? (
        <LoadingSpinner loadingText={loadingText} />
      ) : currentStep === 0 ? (
        <InitialSearchScreen
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
        />
      ) : (
        <MainInteractionScreen
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          setShowResults={setShowResults}
          setHighlightedSection={setHighlightedSection}
          highlightedSection={highlightedSection}
          scrollToSectionAndHighlight={scrollToSectionAndHighlight}
          mmRetrievalRef={mmRetrievalRef}
          groundingRef={groundingRef}
          mmRetrievalGroundingRef={mmRetrievalGroundingRef}
          qaResponses={qaResponses}
        />
      )}
      <UploadModal
        show={showUploadModal}
        onClose={closeUploadModal}
        imageUrlInput={imageUrlInput}
        setImageUrlInput={setImageUrlInput}
        displayedImageUrl={displayedImageUrl}
        setDisplayedImageUrl={setDisplayedImageUrl}
        openEnlargeModal={openEnlargeModal}
        setUploadedImageUrl={setUploadedImageUrl}
      />
      <EnlargeImageModal
        show={showEnlargeModal}
        onClose={closeEnlargeModal}
        imageUrl={enlargedImageUrl}
      />
    </div>
  );
};

export default App;